import {Badge, Textarea} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import {Center} from 'src/design/atoms/Center';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {useMediaQuery} from '@mantine/hooks';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useParams, useSearchParams} from 'react-router-dom';
import type {InspectionDeviceRecordRequest} from 'src/api/ProactApiClient';
import {
  ComplianceStateEnum,
  FeatureFlagEnum,
  ModuleEnum,
  PermissionOperationEnum,
} from 'src/api/ProactApiClient';
import {ProactForm, ProactFormGrid} from 'src/app/forms/engine/ProactForm';
import {useProactForm} from 'src/app/forms/engine/useProactForm';
import {Button} from 'src/design/atoms/Button';
import type {OptionButtonGroupOption} from 'src/design/atoms/OptionButtonGroup';
import {OptionButtonGroup} from 'src/design/atoms/OptionButtonGroup';
import {CancelledIcon, ErrorIcon, SuccessIcon} from 'src/design/atoms/icons/StatusIcons';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {StickyFooter} from 'src/design/molecules/StickyFooter';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {useGetInspectionQuery} from 'src/redux/queries/inspection';
import {
  useGetInspectionDeviceEntryQuery,
  useGetInspectionScopeDevicesQuery,
  useRecordInspectionDeviceMutation,
} from 'src/redux/queries/inspectionRecord';
import {useCanRecordInspectionDevices} from 'src/pages/Modules/Inspections/shared/inspectionHelpers';
import {
  DEVICE_ENTRY_GUIDED_PARAM,
  DEVICE_ENTRY_GUIDED_VALUE,
} from 'src/pages/Modules/Inspections/shared/deviceEntryParams';
import {mapEnumNameToValue, mapEnumValueToName} from 'src/utilities/enumUtils';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {BackBar} from 'src/app/layout/BackBar';
import {OfflineStatusPill} from 'src/app/offline/OfflineStatusPill';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {buildDeviceGroupTree, findNextUnenteredDevice, flattenDeviceGroupTree} from 'src/app/inspections/deviceRollup';
import {
  buildDeviceEntryFormData,
  buildDeviceRecordValues,
  isStaleFieldVersionError,
  mapInspectionDeviceFieldsToFormFieldsV2,
} from '../inspectionRecordMapping';
import {DeviceHistorySidebar} from '../components/DeviceHistorySidebar';
import {DevicePhotosUploader} from '../components/DevicePhotosUploader';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

const RESULT_OPTIONS: OptionButtonGroupOption[] = [
  {value: 'Pass', label: 'Pass', color: 'green', icon: <SuccessIcon />},
  {value: 'Fail', label: 'Fail', color: 'red', icon: <ErrorIcon />},
  {value: 'NoAccess', label: 'No Access', color: 'blue', icon: <CancelledIcon />},
];

const complianceStateToOption = (
  state?: ComplianceStateEnum
): string | null => {
  const name = mapEnumValueToName(ComplianceStateEnum, state);
  return name && name !== 'Invalid' ? name : null;
};

const optionToComplianceState = (
  value: string | null
): ComplianceStateEnum | undefined =>
  mapEnumNameToValue(ComplianceStateEnum, value);

// Draft saves (no RESULT) always allowed; "Save & Next" requires a RESULT +
// passing required-field validation, then advances to the next un-entered device.
export const DeviceEntryPage: React.FC = () => {
  const {id, deviceId} = useParams();
  const inspectionId = id ? Number(id) : undefined;
  const numericDeviceId = deviceId ? Number(deviceId) : undefined;
  const navigate = useAppNavigate();
  const [searchParams] = useSearchParams();
  // Guided-flow entry exits to the execute screen; other filter params
  // (`useDeviceListUrlState`'s q/node/remaining) are preserved either way.
  const cameFromMy =
    searchParams.get(DEVICE_ENTRY_GUIDED_PARAM) === DEVICE_ENTRY_GUIDED_VALUE;
  const backBase = inspectionId
    ? cameFromMy
      ? PathUtility.InspectionsMy(inspectionId)
      : PathUtility.InspectionsView(inspectionId)
    : '';
  const listUrl = `${backBase}?${searchParams.toString()}`;
  const {setAlert} = useAlert();

  const {data: inspection} = useGetInspectionQuery(inspectionId!, {
    skip: !inspectionId,
  });
  const canRecord = useCanRecordInspectionDevices(inspection);
  // Execution happens only via the guided /my flow — entries opened from the admin
  // monitor are always read-only, even for a user who can otherwise record devices.
  const readOnly = !canRecord || !cameFromMy;

  const isWide = useMediaQuery('(min-width: 62em)');

  const {data: devices} = useGetInspectionScopeDevicesQuery(inspectionId!, {
    skip: !inspectionId,
  });
  const orderedDevices = useMemo(
    () => flattenDeviceGroupTree(buildDeviceGroupTree(devices ?? [])),
    [devices]
  );

  // `currentData` (not `data`) so a device A -> B route-param switch clears the
  // entry block mid-fetch, unmounting the photo uploader before B's data lands.
  const {
    currentData: entry,
    isFetching,
    refetch,
  } = useGetInspectionDeviceEntryQuery(
    {inspectionId: inspectionId ?? 0, deviceId: numericDeviceId ?? 0},
    {skip: !inspectionId || !numericDeviceId}
  );

  const [recordDevice, {isLoading: isSaving}] =
    useRecordInspectionDeviceMutation();

  const formMeta = useMemo(
    () => (entry ? mapInspectionDeviceFieldsToFormFieldsV2(entry.fields) : []),
    [entry]
  );

  const [result, setResult] = useState<string | null>(null);

  // No Access relaxes every form-definition field to optional (only Result is required);
  // neither this nor read-only view-field rendering mutates the source formMeta.
  const effectiveMeta = useMemo(
    () =>
      result === 'NoAccess' || readOnly
        ? formMeta.map(field => ({
            ...field,
            required: result === 'NoAccess' ? false : field.required,
            readOnly: readOnly || field.readOnly,
          }))
        : formMeta,
    [formMeta, result, readOnly]
  );

  const form = useProactForm({formMeta: effectiveMeta});
  const {resetForm} = form;
  const [comments, setComments] = useState('');
  const [staleVersionPrompt, setStaleVersionPrompt] = useState(false);
  // Pinned at first load per device — never re-derived from a background
  // refetch of `entry` while the same device stays mounted.
  const [pinnedVersion, setPinnedVersion] = useState<number | undefined>(
    undefined
  );

  const seededForDeviceRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    seededForDeviceRef.current = undefined;
  }, [numericDeviceId]);

  useEffect(() => {
    if (!entry || seededForDeviceRef.current === entry.deviceId) return;
    seededForDeviceRef.current = entry.deviceId;
    resetForm(buildDeviceEntryFormData(formMeta, entry.values));
    setResult(complianceStateToOption(entry.complianceState));
    setComments(entry.comments ?? '');
    setPinnedVersion(entry.effectiveVersion);
  }, [entry, formMeta, resetForm]);

  // No Access strips `required` from effectiveMeta, but a prior failed Save & Next
  // may have left required-field errors on screen — clear them here.
  useEffect(() => {
    if (result === 'NoAccess') {
      form.methods.clearErrors();
    }
  }, [result, form.methods]);

  if (!inspectionId || !numericDeviceId) return null;

  const buildRequest = (
    complianceState: ComplianceStateEnum | undefined
  ): InspectionDeviceRecordRequest => ({
    effectiveVersion: pinnedVersion,
    complianceState,
    comments: comments || undefined,
    values: buildDeviceRecordValues(form.getSubmissionEntries()),
  });

  const submit = async (
    complianceState: ComplianceStateEnum | undefined,
    advance: boolean
  ) => {
    try {
      await recordDevice({
        inspectionId,
        deviceId: numericDeviceId,
        request: buildRequest(complianceState),
      }).unwrap();
      setAlert('Device entry saved.', 'success');

      if (advance) {
        const next = findNextUnenteredDevice(orderedDevices, numericDeviceId);
        if (next) {
          navigate(
            `${PathUtility.InspectionsRecord(inspectionId, next.deviceId)}?${searchParams.toString()}`,
            {replace: true}
          );
        } else {
          navigate(listUrl, {replace: true});
        }
      }
    } catch (error) {
      if (isStaleFieldVersionError(error)) {
        setStaleVersionPrompt(true);
        return;
      }
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  const handleSaveDraft = () => submit(optionToComplianceState(result), false);

  const handleSaveAndNext = async () => {
    if (!result) {
      setAlert(
        'Select Pass, Fail, or No Access before completing this entry.',
        'error'
      );
      return;
    }
    const isValid = await form.validateForm();
    if (!isValid) return;

    await submit(optionToComplianceState(result), true);
  };

  const handleReloadStaleVersion = () => {
    setStaleVersionPrompt(false);
    seededForDeviceRef.current = undefined;
    void refetch();
  };

  const locationBreadcrumb = [entry?.building, entry?.floor, entry?.room]
    .filter(Boolean)
    .join(' · ');

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.Inspections}>
      <Stack gap="lg" pb={80}>
        <BackBar
          fallbackTo={listUrl}
          fallbackLabel="Device list"
          rightSection={<OfflineStatusPill />}
        />

        {isFetching && !entry ? (
          <Center py="xl">
            <Loader size="lg" />
          </Center>
        ) : entry ? (
          <Box
            style={{
              display: 'flex',
              flexDirection: isWide ? 'row' : 'column',
              gap: 'var(--mantine-spacing-lg)',
              alignItems: 'flex-start',
            }}>
            <Stack gap="lg" style={{flex: 1, minWidth: 0}}>
              <Stack gap={4}>
                <Group justify="space-between" wrap="nowrap">
                  <Text size="lg" fw={600}>
                    {entry.deviceName}
                    {entry.serialNumber ? ` · S/N ${entry.serialNumber}` : ''}
                  </Text>
                  {readOnly && <Badge color="gray">Read-only</Badge>}
                </Group>
                {locationBreadcrumb && (
                  <Text size="sm" c="dimmed">
                    {locationBreadcrumb}
                  </Text>
                )}
                <Badge variant="light" color="gray" style={{width: 'fit-content'}}>
                  snapshot · {entry.equipmentTypeName}
                </Badge>
              </Stack>

              <OptionButtonGroup
                label="Result"
                required={!readOnly}
                options={RESULT_OPTIONS}
                value={result}
                onChange={setResult}
                readOnly={readOnly}
              />
              {/* Forward-looking hints only make sense while recording — a
                  read-only review shows outcomes, not consequences. */}
              {!readOnly && result === 'Fail' && (
                <Alert color="red" variant="light">
                  A deficiency will be created on completion.
                </Alert>
              )}
              {!readOnly && result === 'NoAccess' && (
                <Alert color="blue" variant="light">
                  Device not accessible — inspection fields below are
                  optional.
                </Alert>
              )}

              {effectiveMeta.length > 0 && (
                <ProactForm form={form}>
                  <ProactFormGrid />
                </ProactForm>
              )}

              {readOnly ? (
                <Box>
                  <Text fw={500} size="sm" c="dimmed">
                    Comments
                  </Text>
                  <Text>{comments || '—'}</Text>
                </Box>
              ) : (
                <Textarea
                  label="Comments"
                  minRows={2}
                  autosize
                  value={comments}
                  onChange={event => setComments(event.currentTarget.value)}
                />
              )}

              <DevicePhotosUploader
                inspectionId={inspectionId}
                deviceId={numericDeviceId}
                initialAttachments={entry.attachments}
                readOnly={readOnly}
              />

              {!isWide && (
                <Box>
                  <DeviceHistorySidebar
                    deviceId={numericDeviceId}
                    inspectionId={inspectionId}
                  />
                </Box>
              )}
            </Stack>

            {isWide && (
              <Box
                w={300}
                miw={300}
                style={{
                  flexShrink: 0,
                  position: 'sticky',
                  top: 16,
                  alignSelf: 'flex-start',
                  maxHeight: 'calc(100vh - 160px)',
                  overflowY: 'auto',
                  zIndex: 1,
                }}>
                <DeviceHistorySidebar
                  deviceId={numericDeviceId}
                  inspectionId={inspectionId}
                />
              </Box>
            )}
          </Box>
        ) : null}
      </Stack>

      <StickyFooter>
        <Button variant="subtle" color="black" onClick={() => navigate(listUrl)}>
          Close
        </Button>
        {!readOnly && (
          <>
            <Button variant="outline" loading={isSaving} onClick={handleSaveDraft}>
              Save Draft
            </Button>
            <Button color="yellow" loading={isSaving} onClick={handleSaveAndNext}>
              Save &amp; Next
            </Button>
          </>
        )}
      </StickyFooter>

      <ConfirmDialog
        opened={staleVersionPrompt}
        onClose={() => setStaleVersionPrompt(false)}
        onConfirm={handleReloadStaleVersion}
        title="This form has changed"
        body="The inspection form for this device was updated. Reload to get the latest fields before saving."
        buttonTextYes="Reload"
        buttonTextNo="Cancel"
      />
    </ContentWrapper>
  );
};

routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.InspectionsRecord(':id', ':deviceId'),
  element: <DeviceEntryPage />,
  module: ModuleEnum.Inspections,
  operation: PermissionOperationEnum.View,
  isPrivate: true,
  supportsOffline: true,
});
